import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useCartStore } from './cartStore';

const isTokenValid = (jwtToken) => {
    if (!jwtToken) return false;
    try {
        const parts = jwtToken.split('.');
        if (parts.length !== 3) return true;
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        const payload = JSON.parse(jsonPayload);
        if (payload.exp && Date.now() >= payload.exp * 1000) {
            return false;
        }
        return true;
    } catch (e) {
        return true;
    }
};

export const useAuthStore = defineStore('auth', () => {
    const storedToken = localStorage.getItem('customer_token') || null;
    const validInitialToken = isTokenValid(storedToken) ? storedToken : null;
    if (storedToken && !validInitialToken) {
        localStorage.removeItem('customer_token');
        localStorage.removeItem('customer_user');
    }

    let parsedUser = null;
    if (validInitialToken) {
        try {
            parsedUser = JSON.parse(localStorage.getItem('customer_user')) || null;
        } catch (e) {
            parsedUser = null;
        }
    }

    // State
    const token = ref(validInitialToken);
    const user = ref(parsedUser);
    const authModalTrigger = ref(0);
    const authModalMode = ref('login');

    // Getters
    const isAuthenticated = computed(() => !!token.value && isTokenValid(token.value));
    const currentUser = computed(() => user.value);

    // Actions
    const openAuthModal = (mode = 'login') => {
        authModalMode.value = mode;
        authModalTrigger.value += 1;
    };

    const login = async (userData, userToken) => {
        token.value = userToken;
        user.value = userData;
        localStorage.setItem('customer_token', userToken);
        localStorage.setItem('customer_user', JSON.stringify(userData));

        const cartStore = useCartStore();
        await cartStore.syncCartOnLogin();
    };

    const logout = () => {
        token.value = null;
        user.value = null;
        localStorage.removeItem('customer_token');
        localStorage.removeItem('customer_user');

        const cartStore = useCartStore();
        cartStore.clearCart();
    };

    const updateProfile = (newData) => {
        if (user.value) {
            user.value = { ...user.value, ...newData };
            localStorage.setItem('customer_user', JSON.stringify(user.value));
        }
    };

    return {
        token,
        user,
        authModalTrigger,
        authModalMode,
        isAuthenticated,
        currentUser,
        openAuthModal,
        login,
        logout,
        updateProfile
    };
});
